export type MapPoint = { lat: number; lng: number };

function isValidCoord(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

function readPair(lat: unknown, lng: unknown): MapPoint | null {
  const la = typeof lat === "string" ? parseFloat(lat) : lat;
  const ln = typeof lng === "string" ? parseFloat(lng) : lng;
  if (typeof la !== "number" || typeof ln !== "number") return null;
  if (!isValidCoord(la, ln)) return null;
  return { lat: la, lng: ln };
}

/**
 * Resolves map coordinates from a master/listing document.
 * Priority: geo → location → cityLat/cityLng → city.lat/lng → lat/lng
 */
export function resolveMapPoint(
  doc: Record<string, unknown> | null | undefined
): MapPoint | null {
  if (!doc) return null;

  const geo = doc.geo as { lat?: unknown; lng?: unknown } | undefined;
  if (geo) {
    const point = readPair(geo.lat, geo.lng);
    if (point) return point;
  }

  const location = doc.location as { lat?: unknown; lng?: unknown } | undefined;
  if (location) {
    const point = readPair(location.lat, location.lng);
    if (point) return point;
  }

  const cityFlat = readPair(doc.cityLat, doc.cityLng);
  if (cityFlat) return cityFlat;

  const city = doc.city;
  if (city && typeof city === "object" && !Array.isArray(city)) {
    const cityObj = city as { lat?: unknown; lng?: unknown };
    const point = readPair(cityObj.lat, cityObj.lng);
    if (point) return point;
  }

  const topLevel = readPair(doc.lat, doc.lng);
  if (topLevel) return topLevel;

  return null;
}
